import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  Heading,
  SimpleGrid,
  Icon,
  Button,
  Container,
  useDisclosure,
  NativeSelect,
  Input,
  Textarea,
  Badge,
  Alert,
  Flex,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import {
  FaCalendarAlt,
  FaCheck,
  FaTimes,
  FaClock,
  FaMousePointer,
  FaSearchPlus,
  FaEraser,
  FaPlay,
  FaMusic,
  FaChurch,
  FaCompactDisc,
  FaMicrophone,
  FaQuestion,
  FaEye,
  FaEyeSlash,
  FaGlobe,
  FaShareAlt,
} from 'react-icons/fa';
import AppLayout from './AppLayout.jsx';
import { useProductTour } from '../hooks/useProductTour';
import { bandaCalendarioSteps } from '../tours/bandaCalendarioTour';
import CalendarioActividadesToolbar from './CalendarioActividadesToolbar.jsx';
import { disponibilidadAPI, actuacionesAPI, ensayosAPI } from '../services/api';
import {
  applyCalendarioFiltro,
  buildActividadesPorFecha,
  actuacionTieneAcuerdo,
} from '../utils/calendarioFiltros';

// Tipos de eventos disponibles
const TIPOS_EVENTO = [
  { value: 'procesion', label: 'Procesión', icon: FaChurch, color: 'purple' },
  { value: 'concierto', label: 'Concierto', icon: FaMusic, color: 'blue' },
  { value: 'ensayo', label: 'Ensayo', icon: FaPlay, color: 'green' },
  { value: 'grabacion', label: 'Grabación', icon: FaCompactDisc, color: 'red' },
  { value: 'evento_privado', label: 'Evento Privado', icon: FaMicrophone, color: 'orange' },
  { value: 'otro', label: 'Otro', icon: FaQuestion, color: 'gray' },
];

const toLocalDateString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const Calendar = () => {
  const { profile } = useAuth();
  useProductTour('banda-calendario', bandaCalendarioSteps, { autoStart: false });
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  
  const [isLoading, setIsLoading] = useState(false);
  const [actividadesFiltro, setActividadesFiltro] = useState('nada');
  const [actuacionesCal, setActuacionesCal] = useState([]);
  const [ensayosCal, setEnsayosCal] = useState([]);
  const [shareControls, setShareControls] = useState({ onOpen: () => {}, canShare: false });

  // Estados para el calendario de disponibilidad
  
  // Estado para navegación de meses (vista de 1 año)
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const TOTAL_DAYS = 365; // 1 año vista
  
  // Estados para selección de rangos
  const [selectionMode, setSelectionMode] = useState('single'); // 'single' o 'range'
  const [selectedDates, setSelectedDates] = useState([]); // Array de fechas seleccionadas
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);

  // Modal para tipo de evento
  const { open: isEventModalOpen, onOpen: onEventModalOpen, onClose: onEventModalClose } = useDisclosure();
  const [pendingDate, setPendingDate] = useState(null); // Fecha pendiente de confirmar
  const [pendingDates, setPendingDates] = useState([]); // Fechas pendientes (para bulk)
  const [eventType, setEventType] = useState('procesion');
  const [eventNotes, setEventNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Datos de disponibilidad
  const [calendarData, setCalendarData] = useState({
    fechasOcupadas: [], // Array de fechas ocupadas {date: '2024-03-15', evento: 'Concierto', tipo: 'ocupado', tipoEvento: 'procesion'}
    fechasDisponibles: [], // Array de fechas disponibles {date: '2024-03-15', precio: 500, notas: 'Disponible'}
  });

  // Cargar disponibilidad desde la API
  useEffect(() => {
    const loadDisponibilidad = async () => {
      try {
        const currentYear = new Date().getFullYear();
        const response = await disponibilidadAPI.getAll({ ano: currentYear });
        
        if (response.data) {
          const fechasOcupadas = [];
          const fechasDisponibles = [];
          
          response.data.forEach(item => {
            const fecha = toLocalDateString(new Date(item.fecha));
            if (item.estado === 'OCUPADO' || item.estado === 'EVENTO') {
              fechasOcupadas.push({
                date: fecha,
                evento: item.notas || '',
                tipo: item.estado.toLowerCase(),
                tipoEvento: item.tipoEvento || 'otro',
                isPublic: item.isPublic || false,
              });
            } else if (item.estado === 'DISPONIBLE') {
              fechasDisponibles.push({
                date: fecha,
                precio: parseFloat(item.precio) || 0,
                notas: item.notas || '',
              });
            }
          });
          
          setCalendarData({ fechasOcupadas, fechasDisponibles });
        }
      } catch (error) {
        console.error('Error cargando disponibilidad:', error);
        // Mantener datos locales en caso de error
      }
    };

    loadDisponibilidad();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [actsRes, ensRes] = await Promise.all([actuacionesAPI.getAll(), ensayosAPI.getAll()]);
        if (cancelled) return;

        const actsList = Array.isArray(actsRes?.data) ? actsRes.data : [];
        const ensList = Array.isArray(ensRes?.data) ? ensRes.data : [];
        const now = new Date();
        const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

        const acts = actsList
          .filter((a) => a && a.id)
          .filter((a) => {
            if (!a.fecha) return true;
            const d = new Date(a.fecha);
            return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) >= todayUtc;
          })
          .sort((a, b) => {
            const fa = a.fecha ? new Date(a.fecha).getTime() : 0;
            const fb = b.fecha ? new Date(b.fecha).getTime() : 0;
            if (fa !== fb) return fa - fb;
            return String(a.horaInicio || '').localeCompare(String(b.horaInicio || ''));
          });

        const ensayos = ensList
          .filter((e) => e && e.id)
          .filter((e) => (e.estado ? e.estado !== 'CANCELADO' : true))
          .filter((e) => {
            if (!e.fecha) return false;
            const d = new Date(e.fecha);
            return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) >= todayUtc;
          })
          .sort((a, b) => {
            const fa = new Date(a.fecha).getTime();
            const fb = new Date(b.fecha).getTime();
            if (fa !== fb) return fa - fb;
            return String(a.horaInicio || '').localeCompare(String(b.horaInicio || ''));
          });

        setActuacionesCal(acts);
        setEnsayosCal(ensayos);
      } catch {
        // El calendario de disponibilidad sigue funcionando sin actividades
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const actividadesFiltradas = useMemo(
    () => applyCalendarioFiltro(actividadesFiltro, {
      actuaciones: actuacionesCal,
      ensayos: ensayosCal,
    }),
    [actividadesFiltro, actuacionesCal, ensayosCal],
  );

  const actividadesPorFecha = useMemo(
    () => buildActividadesPorFecha(actividadesFiltradas.actuaciones, actividadesFiltradas.ensayos),
    [actividadesFiltradas],
  );

  const actividadesEventCount = actividadesFiltradas.actuaciones.length + actividadesFiltradas.ensayos.length;

  // Función para guardar cambio de disponibilidad
  const saveDisponibilidad = async (fecha, estado) => {
    try {
      await disponibilidadAPI.create({
        fecha,
        estado,
      });
    } catch (error) {
      console.error('Error guardando disponibilidad:', error);
      toaster.create({
        title: 'Error',
        description: 'No se pudo guardar la disponibilidad',
        type: 'error',
        duration: 3000,
      });
    }
  };

  // Funciones para gestión de calendario
  const isDateOccupied = (date) => {
    return calendarData.fechasOcupadas.some(item => item.date === date);
  };

  const isDateAvailable = (date) => {
    return calendarData.fechasDisponibles.some(item => item.date === date);
  };

  const getDateStatus = (date) => {
    if (isDateOccupied(date)) return 'ocupado';
    // Por defecto todos los días están disponibles
    return 'disponible';
  };

  const handleDateClick = async (date) => {
    if (selectionMode === 'single') {
      // Modo de selección individual
      const status = getDateStatus(date);
      
      if (status === 'ocupado') {
        // Si ya está ocupado, liberar la fecha directamente
        setCalendarData(prev => ({
          ...prev,
          fechasOcupadas: prev.fechasOcupadas.filter(item => item.date !== date),
        }));

        try {
          await disponibilidadAPI.create({
            fecha: date,
            estado: 'DISPONIBLE',
          });
          toaster.create({
            title: 'Guardado',
            description: 'Fecha marcada como disponible',
            type: 'success',
            duration: 2000,
            closable: true,
          });
        } catch (error) {
          console.error('Error guardando disponibilidad:', error);
          toaster.create({
            title: 'Error',
            description: 'No se pudo guardar. Los cambios son solo locales.',
            type: 'warning',
            duration: 3000,
          });
        }
      } else {
        // Si está disponible, abrir modal para especificar tipo de evento
        setPendingDate(date);
        setPendingDates([]);
        setEventType('procesion');
        setEventNotes('');
        onEventModalOpen();
      }
    } else {
      // Modo de selección de rangos
      handleRangeSelection(date);
    }
  };

  // Función para confirmar fecha ocupada con tipo de evento
  const handleConfirmOccupied = async () => {
    if (!pendingDate && pendingDates.length === 0) return;
    
    setIsSaving(true);
    
    const tipoEventoLabel = TIPOS_EVENTO.find(t => t.value === eventType)?.label || 'Evento';
    
    try {
      if (pendingDate) {
        // Modo individual
        setCalendarData(prev => ({
          ...prev,
          fechasOcupadas: [
            ...prev.fechasOcupadas.filter(item => item.date !== pendingDate),
            {
              date: pendingDate,
              tipo: 'ocupado',
              tipoEvento: eventType,
              evento: eventNotes || tipoEventoLabel,
              isPublic: false,
            }
          ],
        }));

        await disponibilidadAPI.create({
          fecha: pendingDate,
          estado: 'OCUPADO',
          tipoEvento: eventType,
          notas: eventNotes,
        });
        
        toaster.create({
          title: 'Guardado',
          description: `Fecha marcada como ${tipoEventoLabel}`,
          type: 'success',
          duration: 2000,
          closable: true,
        });
      } else if (pendingDates.length > 0) {
        // Modo bulk
        setCalendarData(prev => {
          const newOcupadas = prev.fechasOcupadas.filter(item => !pendingDates.includes(item.date));
          pendingDates.forEach(date => {
            newOcupadas.push({
              date,
              tipo: 'ocupado',
              tipoEvento: eventType,
              evento: eventNotes || tipoEventoLabel,
              isPublic: false,
            });
          });
          return { ...prev, fechasOcupadas: newOcupadas };
        });

        await disponibilidadAPI.createBulk({
          fechas: pendingDates,
          estado: 'OCUPADO',
          tipoEvento: eventType,
          notas: eventNotes,
        });
        
        toaster.create({
          title: 'Guardado',
          description: `${pendingDates.length} fecha(s) marcadas como ${tipoEventoLabel}`,
          type: 'success',
          duration: 2000,
          closable: true,
        });
        
        clearSelection();
      }
      
      onEventModalClose();
    } catch (error) {
      console.error('Error guardando disponibilidad:', error);
      toaster.create({
        title: 'Error',
        description: 'No se pudo guardar en el servidor.',
        type: 'error',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
      setPendingDate(null);
      setPendingDates([]);
    }
  };

  // Función para obtener info del tipo de evento
  const getEventTypeInfo = (tipoEvento) => {
    return TIPOS_EVENTO.find(t => t.value === tipoEvento) || TIPOS_EVENTO.find(t => t.value === 'otro');
  };

  // Función para obtener el tipo de evento de una fecha
  const getDateEventType = (date) => {
    const ocupada = calendarData.fechasOcupadas.find(item => item.date === date);
    return ocupada?.tipoEvento || null;
  };

  const getOcupadaForDate = (date) => {
    return calendarData.fechasOcupadas.find((item) => item.date === date);
  };

  /** Texto principal del día ocupado (p. ej. acuerdo: "Evento · Cofradía"); si no hay notas, el tipo. */
  const getOccupiedDayTooltipLabel = (date) => {
    const ocupada = getOcupadaForDate(date);
    const tipoInfo = getEventTypeInfo(getDateEventType(date));
    const detalle = (ocupada?.evento && String(ocupada.evento).trim()) || '';
    if (detalle) {
      return (
        <Box maxW="260px">
          <Text fontWeight="700" fontSize="sm" lineHeight="short">
            {detalle}
          </Text>
          <Text fontSize="xs" opacity={0.92} mt={1}>
            {tipoInfo.label}
          </Text>
        </Box>
      );
    }
    return tipoInfo.label;
  };

  const handleRangeSelection = (date) => {
    if (!rangeStart) {
      // Primera fecha del rango
      setRangeStart(date);
      setSelectedDates([date]);
    } else if (!rangeEnd) {
      // Segunda fecha del rango
      if (date === rangeStart) {
        // Si clickea la misma fecha, reiniciar
        setRangeStart(null);
        setSelectedDates([]);
        return;
      }
      
      const start = new Date(rangeStart);
      const end = new Date(date);
      
      // Asegurar que start sea anterior a end
      if (start > end) {
        [start.setTime(end.getTime()), end.setTime(new Date(rangeStart).getTime())];
        setRangeStart(date);
        setRangeEnd(rangeStart);
      } else {
        setRangeEnd(date);
      }
      
      // Crear array de fechas en el rango
      const datesInRange = [];
      const currentDate = new Date(start);
      
      while (currentDate <= end) {
        datesInRange.push(toLocalDateString(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      setSelectedDates(datesInRange);
    } else {
      // Ya hay un rango seleccionado, empezar uno nuevo
      setRangeStart(date);
      setRangeEnd(null);
      setSelectedDates([date]);
    }
  };

  const isDateSelected = (date) => {
    return selectedDates.includes(date);
  };

  const isDateInRange = (date) => {
    if (!rangeStart) return false;
    if (!rangeEnd) return date === rangeStart;
    
    const currentDate = new Date(date);
    const start = new Date(rangeStart);
    const end = new Date(rangeEnd);
    
    return currentDate >= start && currentDate <= end;
  };

  const clearSelection = () => {
    setSelectedDates([]);
    setRangeStart(null);
    setRangeEnd(null);
  };

  const handleBulkAction = async (action) => {
    if (selectedDates.length === 0) return;
    
    if (action === 'ocupar') {
      // Abrir modal para especificar tipo de evento
      setPendingDate(null);
      setPendingDates([...selectedDates]);
      setEventType('procesion');
      setEventNotes('');
      onEventModalOpen();
    } else {
      // Liberar fechas directamente
      setCalendarData(prev => ({
        ...prev,
        fechasOcupadas: prev.fechasOcupadas.filter(item => !selectedDates.includes(item.date)),
      }));

      try {
        await disponibilidadAPI.createBulk({
          fechas: selectedDates,
          estado: 'DISPONIBLE',
        });
        toaster.create({
          title: 'Guardado',
          description: `${selectedDates.length} fecha(s) liberadas correctamente`,
          type: 'success',
          duration: 2000,
          closable: true,
        });
      } catch (error) {
        console.error('Error guardando disponibilidad:', error);
        toaster.create({
          title: 'Error',
          description: 'No se pudo guardar en el servidor.',
          type: 'warning',
          duration: 3000,
        });
      }

      clearSelection();
    }
  };





  // Navegación entre meses
  const goToPreviousMonth = () => {
    const today = new Date();
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    // No permitir ir antes del mes actual
    if (newMonth >= new Date(today.getFullYear(), today.getMonth(), 1)) {
      setCurrentMonth(newMonth);
    }
  };

  const goToNextMonth = () => {
    const today = new Date();
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + TOTAL_DAYS);
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    // No permitir ir más allá del año vista
    if (newMonth <= maxDate) {
      setCurrentMonth(newMonth);
    }
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  // Calcular rango de meses disponibles
  const getAvailableMonths = () => {
    const today = new Date();
    const months = [];
    for (let i = 0; i < 12; i++) {
      const month = new Date(today.getFullYear(), today.getMonth() + i, 1);
      months.push(month);
    }
    return months;
  };

  const generateCalendarDays = () => {
    const today = new Date();
    const days = [];
    
    // Primer día del mes actual
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    
    // Encontrar el lunes de la primera semana del mes
    const startDate = new Date(firstDayOfMonth);
    const dayOfWeek = startDate.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(firstDayOfMonth.getDate() - daysToMonday);
    
    // Generar 6 semanas (42 días) para cubrir cualquier mes
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const dateString = toLocalDateString(date);
      const isCurrentMonth = date.getMonth() === month;
      
      // Verificar si está dentro del rango de 1 año
      const maxDate = new Date(today);
      maxDate.setDate(today.getDate() + TOTAL_DAYS);
      const isInRange = date >= today && date <= maxDate;
      
      days.push({
        date: dateString,
        day: date.getDate(),
        dayName: date.toLocaleDateString('es-ES', { weekday: 'short' }),
        month: date.toLocaleDateString('es-ES', { month: 'short' }),
        status: getDateStatus(dateString),
        isToday: dateString === toLocalDateString(today),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isPastDate: dateString < toLocalDateString(today),
        isCurrentMonth,
        isInRange,
      });
    }
    
    return days;
  };

  // Calcular estadísticas para todo el año
  const getTotalStats = () => {
    const today = new Date();
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + TOTAL_DAYS);
    
    const ocupadasEnRango = calendarData.fechasOcupadas.filter(f => {
      const fecha = new Date(f.date);
      return fecha >= today && fecha <= maxDate;
    }).length;
    
    return {
      total: TOTAL_DAYS,
      ocupadas: ocupadasEnRango,
      disponibles: TOTAL_DAYS - ocupadasEnRango,
    };
  };

  const handleTogglePublic = async (date) => {
    const evento = calendarData.fechasOcupadas.find(e => e.date === date);
    if (!evento) return;
    const newValue = !evento.isPublic;
    setCalendarData(prev => ({
      ...prev,
      fechasOcupadas: prev.fechasOcupadas.map(e =>
        e.date === date ? { ...e, isPublic: newValue } : e
      ),
    }));
    try {
      await disponibilidadAPI.update(date, { isPublic: newValue });
      toaster.create({
        title: newValue ? 'Evento visible' : 'Evento oculto',
        description: newValue
          ? 'Ya aparece en tu perfil público'
          : 'Ya no es visible en tu perfil público',
        type: 'success',
        duration: 2000,
      });
    } catch (error) {
      setCalendarData(prev => ({
        ...prev,
        fechasOcupadas: prev.fechasOcupadas.map(e =>
          e.date === date ? { ...e, isPublic: !newValue } : e
        ),
      }));
      toaster.create({
        title: 'Error',
        description: error?.response?.data?.message || 'No se pudo cambiar la visibilidad',
        type: 'error',
        duration: 4000,
      });
    }
  };

  const handleToggleAllPublic = async (isPublic) => {
    const prevData = calendarData.fechasOcupadas.map(e => ({ ...e }));
    setCalendarData(prev => ({
      ...prev,
      fechasOcupadas: prev.fechasOcupadas.map(e => ({ ...e, isPublic })),
    }));
    try {
      await disponibilidadAPI.togglePublicAll(isPublic);
      toaster.create({
        title: isPublic ? 'Eventos publicados' : 'Eventos ocultados',
        description: isPublic ? 'Todos tus eventos son visibles en tu perfil público' : 'Tus eventos ya no son visibles públicamente',
        type: 'success',
        duration: 2000,
      });
    } catch (error) {
      setCalendarData(prev => ({ ...prev, fechasOcupadas: prevData }));
      toaster.create({
        title: 'Error',
        description: error?.response?.data?.message || 'No se pudo cambiar la visibilidad',
        type: 'error',
        duration: 4000,
      });
    }
  };

  const allEventsPublic = calendarData.fechasOcupadas.length > 0 && calendarData.fechasOcupadas.every(e => e.isPublic);
  const someEventsPublic = calendarData.fechasOcupadas.some(e => e.isPublic);

  return (
    <AppLayout userType="banda" userName={profile?.nombre || 'Mi Banda'}>
      <Box py={{ base: 4, md: 8 }}>
        <Container maxW={{ base: "full", md: "6xl" }} px={{ base: 4, sm: 6, md: 8 }}>
          <VStack gap={{ base: 6, md: 8 }}>
        {/* Resumen de disponibilidad */}
        <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
          <Card.Header data-tour="calendario-header">
            <Heading size="lg">
              <Icon as={FaCalendarAlt} mr={3} color="purple.600" />
              Gestión de disponibilidad
            </Heading>
            <Text fontSize="md" color={textColor} mt={2}>
              Gestiona tu calendario para que las cofradías sepan cuándo estás disponible
            </Text>
          </Card.Header>
          <Card.Body>
            <VStack gap={6}>
              {/* Grid de calendario */}
              <Box w="full" maxW={{ base: "full", sm: "100%", md: "900px", lg: "1100px" }} mx="auto" px={{ base: 2, sm: 0 }}>
                <Box
                  bg="purple.50"
                  border="2px solid"
                  borderColor="purple.200"
                  rounded="xl"
                  p={{ base: 3, md: 4 }}
                  mb={4}
                >
                  <HStack justify="space-between" align="center" w="full" mb={1}>
                    <Text fontSize="md" fontWeight="semibold">
                      Calendario {new Date().getFullYear()}
                    </Text>
                    <Button
                      size="sm"
                      colorPalette="purple"
                      variant="outline"
                      onClick={shareControls.onOpen}
                      disabled={!shareControls.canShare}
                      flexShrink={0}
                      data-tour="calendario-compartir"
                    ><Icon as={FaShareAlt} />Compartir
                                          </Button>
                  </HStack>
                  <Text fontSize="sm" color={textColor}>
                    {selectionMode === 'single'
                      ? 'Haz clic en cualquier día para marcarlo como disponible u ocupado'
                      : 'Haz clic para seleccionar el primer día del rango, luego el último día'}
                  </Text>

                  <Flex
                    justify="space-between"
                    align={{ base: 'stretch', md: 'flex-end' }}
                    w="full"
                    flexWrap="wrap"
                    gap={3}
                    mt={3}
                  >
                    <Box flex="1" minW={{ base: 'full', sm: '200px' }} maxW={{ md: '320px' }}>
                      <CalendarioActividadesToolbar
                        filtro={actividadesFiltro}
                        onFiltroChange={setActividadesFiltro}
                        actuaciones={actividadesFiltradas.actuaciones}
                        ensayos={actividadesFiltradas.ensayos}
                        bandName={profile?.nombre || 'Mi Banda'}
                        eventCount={actividadesEventCount}
                        currentMonth={currentMonth}
                        calendarData={calendarData}
                        showCard={false}
                        onShareControlsReady={setShareControls}
                      />
                    </Box>

                    <Box flexShrink={0} data-tour="calendario-modo">
                      <Text fontSize="xs" color="gray.500" mb={1} fontWeight="600">
                        Modo de selección
                      </Text>
                      <HStack gap={2} flexWrap="wrap">
                        <Button
                          size="sm"
                          variant={selectionMode === 'single' ? 'solid' : 'outline'}
                          colorPalette="purple"
                          onClick={() => {
                            setSelectionMode('single');
                            clearSelection();
                          }}><Icon as={FaMousePointer} />Individual
                                                  </Button>
                        <Button
                          size="sm"
                          variant={selectionMode === 'range' ? 'solid' : 'outline'}
                          colorPalette="purple"
                          onClick={() => {
                            setSelectionMode('range');
                            clearSelection();
                          }}><Icon as={FaSearchPlus} />Rango
                                                  </Button>
                      </HStack>
                    </Box>

                    <SimpleGrid
                      columns={3}
                      gap={2}
                      flexShrink={0}
                      w={{ base: 'full', sm: 'auto' }}
                    >
                      <HStack
                        bg="green.50"
                        px={3}
                        py={2}
                        rounded="lg"
                        borderWidth="1px"
                        borderColor="green.100"
                        justify="center"
                        gap={2}
                      >
                        <Icon as={FaCheck} color="green.500" boxSize={3.5} flexShrink={0} />
                        <VStack gap={0} align="start" lineHeight="1.1">
                          <Text fontSize="md" fontWeight="bold" color="green.600" lineHeight="1.1">
                            {getTotalStats().disponibles}
                          </Text>
                          <Text fontSize="2xs" color="green.700" lineClamp={1}>Disponibles</Text>
                        </VStack>
                      </HStack>
                      <HStack
                        bg="red.50"
                        px={3}
                        py={2}
                        rounded="lg"
                        borderWidth="1px"
                        borderColor="red.100"
                        justify="center"
                        gap={2}
                      >
                        <Icon as={FaTimes} color="red.500" boxSize={3.5} flexShrink={0} />
                        <VStack gap={0} align="start" lineHeight="1.1">
                          <Text fontSize="md" fontWeight="bold" color="red.600" lineHeight="1.1">
                            {getTotalStats().ocupadas}
                          </Text>
                          <Text fontSize="2xs" color="red.700" lineClamp={1}>Ocupados</Text>
                        </VStack>
                      </HStack>
                      <HStack
                        bg="gray.50"
                        px={3}
                        py={2}
                        rounded="lg"
                        borderWidth="1px"
                        borderColor="gray.200"
                        justify="center"
                        gap={2}
                      >
                        <Icon as={FaCalendarAlt} color="gray.500" boxSize={3.5} flexShrink={0} />
                        <VStack gap={0} align="start" lineHeight="1.1">
                          <Text fontSize="md" fontWeight="bold" color="gray.600" lineHeight="1.1">
                            {TOTAL_DAYS}
                          </Text>
                          <Text fontSize="2xs" color="gray.700" lineClamp={1}>Total año</Text>
                        </VStack>
                      </HStack>
                    </SimpleGrid>
                  </Flex>

                  {selectionMode === 'range' && (
                    <VStack gap={2} align="stretch" w="full" mt={3}>
                      {selectedDates.length > 0 && (
                        <HStack gap={2} flexWrap="wrap">
                          <Text fontSize="sm" fontWeight="bold" color="purple.700">
                            {selectedDates.length} fecha(s) seleccionada(s)
                          </Text>
                          <Button size="xs" colorPalette="red" onClick={() => handleBulkAction('ocupar')}><Icon as={FaTimes} />Ocupado
                                                      </Button>
                          <Button
                            size="xs"
                            colorPalette="green"
                            onClick={() => handleBulkAction('liberar')}><Icon as={FaCheck} />Disponible
                                                      </Button>
                          <Button size="xs" variant="outline" onClick={clearSelection}><Icon as={FaEraser} />Limpiar
                                                      </Button>
                        </HStack>
                      )}
                      <Box bg="blue.50" p={2} rounded="md" w="full">
                        <Text fontSize="xs" color="blue.700">
                          <Text as="span" fontWeight="bold">Modo rango:</Text>
                          {!rangeStart ? ' Haz clic en la primera fecha del rango.' :
                           !rangeEnd ? ' Haz clic en la última fecha del rango.' :
                           ` Rango de ${selectedDates.length} días. Haz clic en una nueva fecha para empezar otro rango.`}
                        </Text>
                      </Box>
                    </VStack>
                  )}
                </Box>

                {/* Navegación de meses */}
                <HStack justify="space-between" w="full" mt={4} mb={2} data-tour="calendario-meses">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={goToPreviousMonth}
                    disabled={currentMonth.getMonth() === new Date().getMonth() && currentMonth.getFullYear() === new Date().getFullYear()}
                  >
                    ← Anterior
                  </Button>
                  
                  <HStack gap={2}>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        size="sm"
                        value={`${currentMonth.getFullYear()}-${currentMonth.getMonth()}`}
                        onChange={(e) => {
                          const [year, month] = e.target.value.split('-').map(Number);
                          setCurrentMonth(new Date(year, month, 1));
                        }}
                        w="auto"
                        fontWeight="bold">
                        {getAvailableMonths().map((month, index) => (
                          <option key={index} value={`${month.getFullYear()}-${month.getMonth()}`}>
                            {month.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    <Button size="sm" variant="ghost" colorPalette="purple" onClick={goToToday}>
                      Hoy
                    </Button>
                  </HStack>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={goToNextMonth}
                  >
                    Siguiente →
                  </Button>
                </HStack>
                
                {/* Encabezados de días de la semana */}
                <SimpleGrid 
                  columns={7} 
                  gap={3} 
                  w="full" 
                  mt={4} 
                  display={{ base: "none", sm: "grid" }}
                  justifyItems="center"
                >
                  {['LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SAB', 'DOM'].map((dayName) => (
                    <Box key={dayName} textAlign="center" py={2}>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600" textTransform="uppercase">
                        {dayName}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
                
                <SimpleGrid 
                  columns={{ base: 1, sm: 7 }} 
                  gap={{ base: 2, sm: 3 }} 
                  w="full" 
                  mt={{ base: 4, sm: 1 }}
                  justifyItems="center"
                >
                  {generateCalendarDays().map((day, index) => {
                    const isDisabled = day.isPastDate || !day.isCurrentMonth || !day.isInRange;
                    const dayActs = actividadesPorFecha.get(day.date);
                    const hasActividades = actividadesFiltro !== 'nada' && dayActs
                      && (dayActs.actuaciones.length > 0 || dayActs.ensayos.length > 0);
                    const actividadTooltip = hasActividades ? (
                      <VStack align="start" gap={1} maxW="260px">
                        {dayActs.actuaciones.map((a) => (
                          <Text key={`a-${a.id}`} fontSize="xs">
                            🎺 {a.nombre || 'Actuación'}
                            {a.horaInicio ? ` · ${a.horaInicio}` : ''}
                            {actuacionTieneAcuerdo(a) ? ' (con acuerdo)' : ''}
                          </Text>
                        ))}
                        {dayActs.ensayos.map((e) => (
                          <Text key={`e-${e.id}`} fontSize="xs">
                            🥁 {e.descripcion || e.grupo?.nombre || 'Ensayo'}
                            {e.horaInicio ? ` · ${e.horaInicio}` : ''}
                          </Text>
                        ))}
                      </VStack>
                    ) : null;
                    const totalActs = hasActividades
                      ? dayActs.actuaciones.length + dayActs.ensayos.length
                      : 0;
                    return (
                      <Tooltip
                        key={index}
                        content={actividadTooltip}
                        disabled={!hasActividades}
                        showArrow
                        openDelay={300}
                        positioning={{
                          placement: "top"
                        }}
                      >
                        <VStack
                          gap={{ base: 1, sm: 1 }}
                          p={{ base: 2, sm: 2, md: 2 }}
                          rounded="lg"
                          border="2px solid"
                          borderColor={
                            !day.isCurrentMonth ? 'transparent' :
                            isDateSelected(day.date) ? 'purple.500' :
                            day.status === 'ocupado' ? 'red.300' :
                            day.status === 'disponible' ? 'green.300' :
                            isDisabled ? 'gray.100' :
                            'gray.200'
                          }
                          bg={
                            !day.isCurrentMonth ? 'transparent' :
                            isDateSelected(day.date) ? 'purple.100' :
                            day.status === 'ocupado' ? 'red.50' :
                            day.status === 'disponible' ? 'green.50' :
                            day.isToday ? 'blue.50' :
                            isDisabled ? 'gray.50' :
                            'white'
                          }
                          opacity={!day.isCurrentMonth ? 0.3 : isDisabled ? 0.5 : 1}
                          _hover={isDisabled || !day.isCurrentMonth ? {} : {
                            transform: 'translateY(-2px)',
                            shadow: 'lg',
                            borderColor: 'purple.400',
                            bg: isDateSelected(day.date) ? 'purple.200' :
                                day.status === 'ocupado' ? 'red.100' :
                                day.status === 'disponible' ? 'green.100' :
                                'purple.50'
                          }}
                          transition="all 0.3s ease"
                          onClick={() => !isDisabled && day.isCurrentMonth && handleDateClick(day.date)}
                          cursor={isDisabled || !day.isCurrentMonth ? 'not-allowed' : 'pointer'}
                          position="relative"
                          h={{ base: "auto", sm: hasActividades ? "100px" : "80px", md: hasActividades ? "110px" : "90px", lg: hasActividades ? "120px" : "100px" }}
                          w="full"
                          minW="0"
                          display={{ base: day.isCurrentMonth ? "flex" : "none", sm: "flex" }}
                        >
                          <Text fontSize={{ base: "lg", sm: "xl", md: "2xl" }} fontWeight="bold" color={
                            !day.isCurrentMonth ? 'gray.300' :
                            day.status === 'ocupado' ? 'red.600' :
                            day.status === 'disponible' ? 'green.600' :
                            'gray.700'
                          }>
                            {day.day}
                          </Text>
                          
                          {/* Indicador de estado accesible (icono + texto para daltónicos) */}
                          {day.isCurrentMonth && day.isInRange && (
                            day.status === 'ocupado' ? (
                              <Tooltip
                                content={getOccupiedDayTooltipLabel(day.date)}
                                showArrow
                                openDelay={400}
                                positioning={{
                                  placement: "top"
                                }}
                              >
                                <VStack gap={0.5} align="center" w="full" px={0.5}>
                                  <HStack gap={1}>
                                    <Icon as={FaTimes} w={3} h={3} color="red.600" />
                                    <Icon
                                      as={getEventTypeInfo(getDateEventType(day.date)).icon}
                                      w={3}
                                      h={3}
                                      color={`${getEventTypeInfo(getDateEventType(day.date)).color}.500`}
                                    />
                                  </HStack>
                                  {(() => {
                                    const o = getOcupadaForDate(day.date);
                                    const t = (o?.evento && String(o.evento).trim()) || '';
                                    if (!t) return null;
                                    return (
                                      <Text
                                        fontSize="2xs"
                                        fontWeight="semibold"
                                        color="red.700"
                                        lineClamp={2}
                                        textAlign="center"
                                        lineHeight="1.2"
                                        w="full"
                                      >
                                        {t}
                                      </Text>
                                    );
                                  })()}
                                </VStack>
                              </Tooltip>
                            ) : !day.isPastDate && (
                              <Icon
                                as={FaCheck}
                                w={3}
                                h={3}
                                color="green.600"
                              />
                            )
                          )}

                          {/* Actuaciones y ensayos (según filtro) */}
                          {hasActividades && (
                            <VStack gap={0.5} w="full" px={0.5} align="stretch" flex={1} minH={0} overflow="hidden">
                              {dayActs.actuaciones.slice(0, 1).map((a) => (
                                <Badge
                                  key={a.id}
                                  colorPalette={actuacionTieneAcuerdo(a) ? 'blue' : 'orange'}
                                  fontSize="2xs"
                                  px={1}
                                  py={0.5}
                                  rounded="sm"
                                  lineClamp={1}
                                  w="full"
                                  textAlign="left"
                                >
                                  {a.nombre || 'Actuación'}
                                </Badge>
                              ))}
                              {dayActs.ensayos.slice(0, 1).map((e) => (
                                <Badge
                                  key={e.id}
                                  colorPalette="pink"
                                  fontSize="2xs"
                                  px={1}
                                  py={0.5}
                                  rounded="sm"
                                  lineClamp={1}
                                  w="full"
                                  textAlign="left"
                                >
                                  {e.descripcion || e.grupo?.nombre || 'Ensayo'}
                                </Badge>
                              ))}
                              {totalActs > (Math.min(dayActs.actuaciones.length, 1) + Math.min(dayActs.ensayos.length, 1)) && (
                                <Text fontSize="2xs" color="gray.500" textAlign="center">
                                  +{totalActs - Math.min(dayActs.actuaciones.length, 1) - Math.min(dayActs.ensayos.length, 1)} más
                                </Text>
                              )}
                            </VStack>
                          )}

                          {/* Indicador de hoy */}
                          {day.isToday && (
                            <Badge
                              position="absolute"
                              top={1}
                              right={1}
                              colorPalette="blue"
                              fontSize="2xs"
                              px={1}
                            >
                              HOY
                            </Badge>
                          )}

                          {/* Indicador de selección */}
                          {isDateSelected(day.date) && (
                            <Box
                              position="absolute"
                              top="50%"
                              left="50%"
                              transform="translate(-50%, -50%)"
                              w={8}
                              h={8}
                              border="3px solid"
                              borderColor="purple.500"
                              rounded="full"
                              bg="purple.100"
                              opacity={0.8}
                              pointerEvents="none"
                            />
                          )}
                        </VStack>
                      </Tooltip>
                    );
                  })}
                </SimpleGrid>
              </Box>

              {/* Leyenda simplificada y accesible */}
              <Card.Root bg="gray.50" w="full">
                <Card.Body>
                  <VStack gap={3} align="start">
                    <Text fontSize="md" fontWeight="bold">Leyenda:</Text>
                    <SimpleGrid columns={{ base: 2, md: 4 }} gap={4} w="full">
                      <HStack gap={2}>
                        <Badge colorPalette="blue" fontSize="xs">HOY</Badge>
                        <Text fontSize="sm" fontWeight="medium">Día actual</Text>
                      </HStack>
                      <HStack gap={2}>
                        <Box bg="green.50" border="2px solid" borderColor="green.300" rounded="md" px={2} py={1}>
                          <Icon as={FaCheck} w={3} h={3} color="green.600" />
                        </Box>
                        <Text fontSize="sm" fontWeight="medium">Disponible</Text>
                      </HStack>
                      <HStack gap={2}>
                        <Box bg="red.50" border="2px solid" borderColor="red.300" rounded="md" px={2} py={1}>
                          <Icon as={FaTimes} w={3} h={3} color="red.600" />
                        </Box>
                        <Text fontSize="sm" fontWeight="medium">Ocupado</Text>
                      </HStack>
                      <HStack gap={2}>
                        <Box 
                          border="3px solid" 
                          borderColor="purple.500" 
                          rounded="md" 
                          bg="purple.100"
                          px={2}
                          py={1}
                        >
                          <Text fontSize="xs" color="purple.700">12</Text>
                        </Box>
                        <Text fontSize="sm" fontWeight="medium">Seleccionado</Text>
                      </HStack>
                      {actividadesFiltro !== 'nada' && (
                        <>
                          <HStack gap={2}>
                            <Badge colorPalette="blue" fontSize="2xs">Act.</Badge>
                            <Text fontSize="sm" fontWeight="medium">Actuación con acuerdo</Text>
                          </HStack>
                          <HStack gap={2}>
                            <Badge colorPalette="orange" fontSize="2xs">Act.</Badge>
                            <Text fontSize="sm" fontWeight="medium">Actuación sin acuerdo</Text>
                          </HStack>
                          <HStack gap={2}>
                            <Badge colorPalette="pink" fontSize="2xs">Ens.</Badge>
                            <Text fontSize="sm" fontWeight="medium">Ensayo</Text>
                          </HStack>
                        </>
                      )}
                    </SimpleGrid>
                  </VStack>
                </Card.Body>
              </Card.Root>

              {/* Próximos eventos */}
              {calendarData.fechasOcupadas.length > 0 && (
                <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                  <Card.Header>
                    <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
                      <Heading size="md">
                        <Icon as={FaClock} mr={2} color="red.500" />
                        Próximos eventos programados
                      </Heading>
                      <Button
                        size="sm"
                        variant={allEventsPublic ? 'solid' : 'outline'}
                        colorPalette={allEventsPublic ? 'green' : 'gray'}
                        onClick={() => handleToggleAllPublic(!allEventsPublic)}
                        rounded="full"><Icon as={allEventsPublic ? FaEye : FaEyeSlash} />{allEventsPublic ? 'Ocultar todos' : 'Hacer visibles todos'}</Button>
                    </HStack>
                    <Alert.Root status="info" rounded="lg" mt={3} fontSize="sm" py={2}>
                      <Alert.Indicator boxSize={4} />
                      <Text fontSize="xs">
                        Los eventos visibles se mostrarán en tu <strong>perfil público</strong> para que cofradías y otros usuarios puedan ver tu agenda.
                      </Text>
                    </Alert.Root>
                  </Card.Header>
                  <Card.Body>
                    <VStack gap={3} align="start">
                      {calendarData.fechasOcupadas
                        .filter(evento => new Date(evento.date) >= new Date())
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .slice(0, 5)
                        .map((evento, index) => {
                          const eventTypeInfo = getEventTypeInfo(evento.tipoEvento);
                          return (
                            <HStack key={index} w="full" p={3} bg={`${eventTypeInfo.color}.50`} rounded="md" borderLeft="4px solid" borderColor={`${eventTypeInfo.color}.500`}>
                              <Icon as={eventTypeInfo.icon} color={`${eventTypeInfo.color}.500`} boxSize={5} />
                              <VStack gap={0} align="start" flex={1}>
                                <HStack gap={2}>
                                  <Text fontSize="sm" fontWeight="bold">
                                    {new Date(evento.date).toLocaleDateString('es-ES', {
                                      weekday: 'long',
                                      day: 'numeric',
                                      month: 'long'
                                    })}
                                  </Text>
                                  <Badge colorPalette={eventTypeInfo.color} size="sm">
                                    {eventTypeInfo.label}
                                  </Badge>
                                </HStack>
                                {evento.evento && (
                                  <Text fontSize="sm" color="gray.600">
                                    {evento.evento}
                                  </Text>
                                )}
                              </VStack>
                              <Button
                                size="xs"
                                variant={evento.isPublic ? 'solid' : 'outline'}
                                colorPalette={evento.isPublic ? 'green' : 'gray'}
                                onClick={() => handleTogglePublic(evento.date)}
                                rounded="full"
                                fontWeight="normal"
                                minW="auto"
                                whiteSpace="nowrap"><Icon as={evento.isPublic ? FaEye : FaEyeSlash} />{evento.isPublic ? 'Ocultar' : 'Hacer visible'}</Button>
                            </HStack>
                          );
                        })}
                    </VStack>
                  </Card.Body>
                </Card.Root>
              )}
            </VStack>
          </Card.Body>
        </Card.Root>
      </VStack>

      {/* Modal para tipo de evento */}
      <Dialog.Root open={isEventModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onEventModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>
                <HStack gap={2}>
                  <Icon as={FaCalendarAlt} color="purple.500" />
                  <Text>Marcar fecha como ocupada</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack gap={4}>
                  {pendingDate && (
                    <Box w="full" bg="purple.50" p={3} rounded="md">
                      <Text fontWeight="bold" color="purple.700">
                        {new Date(pendingDate).toLocaleDateString('es-ES', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </Text>
                    </Box>
                  )}
                  
                  {pendingDates.length > 0 && (
                    <Box w="full" bg="purple.50" p={3} rounded="md">
                      <Text fontWeight="bold" color="purple.700">
                        {pendingDates.length} fecha(s) seleccionadas
                      </Text>
                      <Text fontSize="sm" color="purple.600">
                        {pendingDates.slice(0, 3).map(d => 
                          new Date(d).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
                        ).join(', ')}
                        {pendingDates.length > 3 && ` y ${pendingDates.length - 3} más...`}
                      </Text>
                    </Box>
                  )}

                  <Field.Root>
                    <Field.Label>Tipo de evento</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        value={eventType}
                        onChange={(e) => setEventType(e.target.value)}
                        size="lg">
                        {TIPOS_EVENTO.map(tipo => (
                          <option key={tipo.value} value={tipo.value}>
                            {tipo.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>

                  {/* Previsualización del tipo seleccionado */}
                  <HStack w="full" p={3} bg={`${getEventTypeInfo(eventType).color}.50`} rounded="md" justify="center">
                    <Icon as={getEventTypeInfo(eventType).icon} color={`${getEventTypeInfo(eventType).color}.500`} boxSize={6} />
                    <Text fontWeight="bold" color={`${getEventTypeInfo(eventType).color}.700`}>
                      {getEventTypeInfo(eventType).label}
                    </Text>
                  </HStack>

                  <Field.Root>
                    <Field.Label>Notas (opcional)</Field.Label>
                    <Textarea 
                      value={eventNotes}
                      onChange={(e) => setEventNotes(e.target.value)}
                      placeholder="Ej: Procesión del Nazareno, Concierto benéfico..."
                      rows={2}
                    />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onEventModalClose}>
                  Cancelar
                </Button>
                <Button 
                  colorPalette="purple" 
                  onClick={handleConfirmOccupied}
                  loading={isSaving}
                  loadingText="Guardando..."
                >
                  Confirmar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
        </Container>
      </Box>
    </AppLayout>
  );
};

export default Calendar;
